import * as React from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@repo/ui";
import { Loader2, User, UserPlus, MapPin, Calendar } from "lucide-react";
import { cn } from "@repo/utils";
import { DateInput } from "@/components/forms/date-input";
import { SidePanel } from "@/components/side-panel";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import type { ParsedAddress } from "@/components/ui/address-autocomplete";
import type { Patient, CreatePatientInput } from "@/lib/types/patients";
import { createPatient, updatePatient } from "@/lib/api/patients";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";

// -------------------------------------------------------
// Constants
// -------------------------------------------------------

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------

function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Auto-format digits into (###) ###-#### as the user types. */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatPatientName(patient: Patient): string {
  const parts = [patient.first_name];
  if (patient.middle_name) parts.push(patient.middle_name);
  if (patient.last_name) parts.push(patient.last_name);
  return parts.join(" ");
}

const emptyForm: CreatePatientInput = {
  first_name: "",
  middle_name: null,
  last_name: null,
  date_of_birth: null,
  phone_number: null,
  street: null,
  street_2: null,
  city: null,
  state: null,
  zip: null,
};

/** Parse a search string like "John Doe" into first/last name fields. */
function parseDefaultName(name: string): CreatePatientInput {
  const words = name.trim().split(/\s+/);
  return {
    ...emptyForm,
    first_name: capitalize(words[0] || ""),
    last_name: words.length > 1 ? capitalize(words.slice(1).join(" ")) : null,
  };
}

// -------------------------------------------------------
// Props
// -------------------------------------------------------

export interface PatientManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, modal opens in edit mode for this patient. */
  patient?: Patient | null;
  /** Pre-fill name fields when creating from a combobox search string. */
  defaultName?: string;
  onPatientsChange?: () => void;
  /** Called after a new patient is created — returns the created patient so callers can auto-select. */
  onCreated?: (patient: Patient) => void;
}

// -------------------------------------------------------
// Component
// -------------------------------------------------------

export function PatientManagementModal({
  open,
  onOpenChange,
  patient,
  defaultName,
  onPatientsChange,
  onCreated,
}: PatientManagementModalProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();

  const isEdit = !!patient;

  // Form state
  const [form, setForm] = React.useState<CreatePatientInput>(emptyForm);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Reset form when modal opens / patient changes
  React.useEffect(() => {
    if (open) {
      if (patient) {
        setForm({
          first_name: patient.first_name,
          middle_name: patient.middle_name,
          last_name: patient.last_name,
          date_of_birth: patient.date_of_birth,
          phone_number: patient.phone_number
            ? formatPhoneNumber(patient.phone_number)
            : null,
          street: patient.street,
          street_2: patient.street_2,
          city: patient.city,
          state: patient.state,
          zip: patient.zip,
        });
      } else if (defaultName) {
        setForm(parseDefaultName(defaultName));
      } else {
        setForm(emptyForm);
      }
      setErrors({});
    }
  }, [open, patient, defaultName]);

  // -------------------------------------------------------
  // Form helpers
  // -------------------------------------------------------

  const nameFields = new Set<keyof CreatePatientInput>([
    "first_name",
    "middle_name",
    "last_name",
  ]);

  const updateField = (field: keyof CreatePatientInput, value: string) => {
    const finalValue = nameFields.has(field) ? capitalize(value) : value;
    setForm((prev) => ({ ...prev, [field]: finalValue || null }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    setForm((prev) => ({
      ...prev,
      street: address.street || prev.street,
      city: address.city || prev.city,
      state: address.state || prev.state,
      zip: address.zip || prev.zip,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!form.last_name?.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (!form.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required";
    } else {
      const dob = new Date(form.date_of_birth + "T00:00:00");
      if (isNaN(dob.getTime())) {
        newErrors.date_of_birth = "Invalid date";
      } else if (dob > new Date()) {
        newErrors.date_of_birth = "Date of birth cannot be in the future";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------------------------------------------
  // Submit
  // -------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    const payload: CreatePatientInput = {
      first_name: form.first_name.trim(),
      middle_name: form.middle_name?.trim() || null,
      last_name: form.last_name?.trim() || null,
      date_of_birth: form.date_of_birth || null,
      phone_number: form.phone_number?.trim() || null,
      street: form.street?.trim() || null,
      street_2: form.street_2?.trim() || null,
      city: form.city?.trim() || null,
      state: form.state?.trim() || null,
      zip: form.zip?.trim() || null,
    };

    try {
      if (isEdit && patient) {
        const data = await updatePatient(patient.id, payload);
        toast({
          title: "Patient Updated",
          description: `${formatPatientName(data.data.patient)} has been updated.`,
          variant: "success",
        });
      } else {
        const data = await createPatient(payload);
        const newPatient = data.data.patient;
        toast({
          title: "Patient Created",
          description: `${formatPatientName(newPatient)} has been added.`,
          variant: "success",
        });
        onCreated?.(newPatient);
      }

      onPatientsChange?.();
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiClientError) {
        const handled = handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async (token) => {
            const retryPayload = {
              ...payload,
              approvalToken: token,
            };
            if (isEdit && patient) {
              const data = await updatePatient(patient.id, retryPayload);
              toast({
                title: "Patient Updated",
                description: `${formatPatientName(data.data.patient)} has been updated.`,
                variant: "success",
              });
            } else {
              const data = await createPatient(retryPayload);
              onCreated?.(data.data.patient);
              toast({
                title: "Patient Created",
                description: `${formatPatientName(data.data.patient)} has been added.`,
                variant: "success",
              });
            }
            onPatientsChange?.();
            onOpenChange(false);
          },
          actionDescription: isEdit ? "Update patient" : "Create patient",
        });
        if (handled) return;
      }
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${isEdit ? "update" : "create"} patient`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      icon={isEdit ? User : UserPlus}
      title={isEdit ? "Edit Patient" : "New Patient"}
      description={
        isEdit
          ? `Update information for ${formatPatientName(patient!)}. First name, last name, and date of birth are required.`
          : "Fill in the patient's information. First name, last name, and date of birth are required."
      }
      maxWidthClassName="sm:max-w-2xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="patient-form"
            disabled={isSaving}
            data-testid="patient-form-submit"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEdit ? "Saving..." : "Creating..."}
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Patient"
            )}
          </Button>
        </div>
      }
    >
      <form id="patient-form" onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* ---- Name Section ---- */}
          <fieldset className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              Patient Name
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="patient-first-name">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="patient-first-name"
                  placeholder="John"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      first_name: capitalize(e.target.value),
                    }))
                  }
                  disabled={isSaving}
                  autoFocus
                  className={cn(errors.first_name && "border-destructive")}
                  data-testid="patient-form-first-name"
                />
                {errors.first_name && (
                  <p className="text-xs text-destructive">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="patient-middle-name">
                  Middle Name{" "}
                  <span className="text-muted-foreground font-normal">
                    (Optional)
                  </span>
                </Label>
                <Input
                  id="patient-middle-name"
                  placeholder="M."
                  value={form.middle_name || ""}
                  onChange={(e) => updateField("middle_name", e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="patient-last-name">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="patient-last-name"
                  placeholder="Doe"
                  value={form.last_name || ""}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  disabled={isSaving}
                  className={cn(errors.last_name && "border-destructive")}
                  data-testid="patient-form-last-name"
                />
                {errors.last_name && (
                  <p className="text-xs text-destructive">{errors.last_name}</p>
                )}
              </div>
            </div>
          </fieldset>

          <div className="border-t border-border/50" />

          {/* ---- Date of Birth & Phone ---- */}
          <fieldset className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              Details
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="patient-dob">
                  Date of Birth <span className="text-destructive">*</span>
                </Label>
                <DateInput
                  id="patient-dob"
                  data-testid="patient-form-dob"
                  value={form.date_of_birth || ""}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      date_of_birth: value || null,
                    }));
                    if (errors.date_of_birth) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.date_of_birth;
                        return next;
                      });
                    }
                  }}
                  disabled={isSaving}
                  className={cn(errors.date_of_birth && "border-destructive")}
                />
                {errors.date_of_birth && (
                  <p className="text-xs text-destructive">
                    {errors.date_of_birth}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="patient-phone">
                  Phone Number{" "}
                  <span className="text-muted-foreground font-normal">
                    (Optional)
                  </span>
                </Label>
                <Input
                  id="patient-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone_number || ""}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      phone_number: formatted || null,
                    }));
                    if (errors.phone_number) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.phone_number;
                        return next;
                      });
                    }
                  }}
                  disabled={isSaving}
                />
              </div>
            </div>
          </fieldset>

          <div className="border-t border-border/50" />

          {/* ---- Address Section ---- */}
          <fieldset className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              Address
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                <div className="space-y-1.5">
                  <Label htmlFor="patient-street">
                    Street Address{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </Label>
                  <AddressAutocomplete
                    id="patient-street"
                    value={form.street || ""}
                    onChange={(val) => updateField("street", val)}
                    onAddressSelect={handleAddressSelect}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-1.5 sm:w-[140px]">
                  <Label htmlFor="patient-street2">
                    Apt/Suite{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </Label>
                  <Input
                    id="patient-street2"
                    placeholder="Apt 4B"
                    value={form.street_2 || ""}
                    onChange={(e) => updateField("street_2", e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="patient-city">City</Label>
                  <Input
                    id="patient-city"
                    placeholder="Springfield"
                    value={form.city || ""}
                    onChange={(e) => updateField("city", e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="patient-state">State</Label>
                  <Select
                    value={form.state || ""}
                    onValueChange={(val) => updateField("state", val)}
                    disabled={isSaving}
                  >
                    <SelectTrigger id="patient-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="patient-zip">ZIP Code</Label>
                  <Input
                    id="patient-zip"
                    placeholder="12345"
                    value={form.zip || ""}
                    onChange={(e) => updateField("zip", e.target.value)}
                    disabled={isSaving}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </form>
    </SidePanel>
  );
}
